/**
 * The app's data flows. Components call these; nothing here renders.
 */
import {
  type CallKind,
  type CallTokenResponse,
  type ExportView,
  type MediaResponse,
  type MediaView,
  type MessageView,
  type PollView,
  type PrivacyRequest,
  type UserResponse,
  type UserView,
} from '@olmt/shared'
import { api } from './api'
import { LIVEKIT_URL } from './env'
import { disconnectSocket, getSocket, sendRead } from './socket'
import { blip } from './sounds'
import { uploadWithProgress } from './upload'
import { useAuthStore } from '../stores/authStore'
import { useCallStore } from '../stores/callStore'
import { useChatStore } from '../stores/chatStore'
import { useSearchStore } from '../stores/searchStore'
import { mentionedHandles } from './mentions'
import { type PollDraft } from './polls'
import { useUiStore } from '../stores/uiStore'

const PAGE_SIZE = 50

export async function bootstrapSession(): Promise<void> {
  const { user, privacy } = await api.me()
  useAuthStore.getState().setUser(user)
  // W13: the boot read is where the account screen's switches learn their state, so a toggle turned
  // off on another device is off here from the first render.
  useAuthStore.getState().setPrivacy(privacy)
  const { chats } = await api.chats()
  useChatStore.getState().setChats(chats)
}

export async function openChat(chatId: string): Promise<void> {
  const { chat } = await api.chat(chatId)
  useChatStore.getState().upsertChat(chat)
  if (useChatStore.getState().loaded[chatId]) return
  const page = await api.messages(chatId, { limit: PAGE_SIZE })
  useChatStore.getState().setMessages(chatId, page.messages, page.hasMore)
  const newest = page.messages[page.messages.length - 1]
  if (newest && newest.authorId !== useAuthStore.getState().user?.id) {
    void api.markRead(chatId, { messageId: newest.id }).catch(() => undefined)
    useChatStore.getState().clearUnread(chatId, newest.id)
  }
}

export async function loadOlderMessages(chatId: string): Promise<void> {
  const state = useChatStore.getState()
  const oldest = state.messages[chatId]?.[0]
  if (!oldest || !state.hasMore[chatId]) return
  const page = await api.messages(chatId, { before: oldest.id, limit: PAGE_SIZE })
  useChatStore.getState().prependMessages(chatId, page.messages, page.hasMore)
}

/**
 * How far a search jump (W3) pages back before it stops: 20 pages of `PAGE_SIZE`. The newest page
 * `openChat` loads is the first of them, so the cap is 20 pages of history rather than 21.
 */
const JUMP_PAGE_CAP = 20

/**
 * The landing a search hit asks for (W3): load the newest page, then keep prepending older ones
 * until the message is in hand or the chat has no more, and mark it for highlighting when it is.
 *
 * `'too-old'` is the honest answer for both ways of not reaching it — the cap was hit, or the whole
 * history is loaded and the message is not in it — so the caller shows the notice instead of
 * pretending the jump worked. It throws only when a page itself fails; "did not reach it" is a
 * result, not an error.
 */
export async function openChatAt(chatId: string, messageId: string): Promise<'found' | 'too-old'> {
  await openChat(chatId)
  let message =
    useChatStore.getState().messages[chatId]?.find((entry) => entry.id === messageId) ?? null
  let pages = 1
  while (!message && useChatStore.getState().hasMore[chatId] && pages < JUMP_PAGE_CAP) {
    await loadOlderMessages(chatId)
    pages++
    message = useChatStore.getState().messages[chatId]?.find((entry) => entry.id === messageId) ?? null
  }
  if (!message) return 'too-old'
  useChatStore.getState().setHighlight(messageId)
  return 'found'
}

function optimisticMessage(input: {
  chatId: string
  clientId: string
  kind: MessageView['kind']
  body: string | null
  media: MediaView | null
  replyToId: string | null
  mentions: string[]
  poll: PollView | null
}): MessageView {
  const user = useAuthStore.getState().user
  return {
    id: `pending:${input.clientId}`,
    chatId: input.chatId,
    authorId: user?.id ?? '',
    author: user,
    kind: input.kind,
    body: input.body,
    media: input.media,
    replyToId: input.replyToId,
    callId: null,
    editedAt: null,
    forwarded: false,
    reactions: [],
    mentions: input.mentions,
    poll: input.poll,
    deletedAt: null,
    createdAt: Date.now(),
    clientId: input.clientId,
    pending: true,
  }
}

/**
 * Persists a message and shows it immediately. The socket broadcasts the stored row back to the
 * sender too, so the optimistic copy is reconciled by `clientId` (docs/CONTRACT.md §5).
 * Throws on failure; the composer restores the draft text.
 */
export async function sendMessage(input: {
  chatId: string
  kind: MessageView['kind']
  body?: string | null
  mediaId?: string | null
  media?: MediaView | null
  replyToId?: string | null
  /** W11: required for `kind: 'poll'` — the options; the question is the body. */
  poll?: { options: string[]; multiple?: boolean }
}): Promise<void> {
  const clientId = crypto.randomUUID()
  const body = input.body ?? null
  const media = input.media ?? null
  const replyToId = input.replyToId ?? null
  const poll = input.poll ?? null
  const store = useChatStore.getState()
  /**
   * W10 — the members this message names, read off the body *here*, at the one moment a body
   * becomes a stored message. The picker keeps no set to drift away from the words that are
   * actually sent: a handle that was backspaced away names nobody, and one that was typed by hand
   * names its member. The chat's members are the same list the picker offered; a chat that is not
   * loaded offers nobody, and the api refuses an id that is not a member anyway (400).
   */
  const mentions = mentionedHandles(
    body ?? '',
    store.chats[input.chatId]?.members ?? [],
  ).map((member) => member.id)
  store.upsertMessage(
    optimisticMessage({
      chatId: input.chatId,
      clientId,
      kind: input.kind,
      body,
      media,
      replyToId,
      mentions,
      // W11: the options the server will store (trimmed, like the api stores them) and no votes
      // yet. The copy is `pending`, so its rows show the tallies but cannot be voted on until this
      // reconciles with the stored row — the id a vote needs does not exist before that.
      poll:
        poll === null
          ? null
          : {
              multiple: poll.multiple ?? false,
              options: poll.options.map((text, index) => ({ index, text: text.trim(), voterIds: [] })),
            },
    }),
  )

  const payload = {
    kind: input.kind,
    body,
    mediaId: input.mediaId ?? null,
    clientId,
    replyToId,
  }
  const socket = getSocket()
  // W10: the frozen `message.send` frame carries no `mentions` (docs/CONTRACT.md §5), so a message
  // that names members takes the REST path — the ids are what the mention rows and the mute-exempt
  // push are made of, and a mention that arrived without them would be text nobody is told about.
  // W11: a poll takes the REST path for the same reason — the frame carries no `poll` either, and
  // a question whose options were dropped is not a poll at all.
  if (
    poll === null &&
    mentions.length === 0 &&
    socket?.state === 'open' &&
    socket.sendMessage({ chatId: input.chatId, ...payload })
  ) {
    blip('sent')
    return
  }

  try {
    const { message } = await api.sendMessage(input.chatId, {
      ...payload,
      ...(mentions.length === 0 ? {} : { mentions }),
      ...(poll === null ? {} : { poll }),
    })
    useChatStore.getState().upsertMessage(message)
    // The tick lands when the send is accepted, not on the press: a failure removes the bubble and
    // must not have been accompanied by a confident sound.
    blip('sent')
  } catch (error) {
    useChatStore.getState().removeMessage(input.chatId, `pending:${clientId}`)
    throw error
  }
}

/**
 * W11 — a poll is a message whose body is the question. The api owns the bounds (2–10 options, each
 * non-blank and at most 80 characters, a non-blank question) and the dialog refuses the same
 * problems before it can send, so this path only ever carries a valid draft. Throws, so the dialog
 * keeps every field and says the send failed.
 */
export async function createPoll(chatId: string, draft: PollDraft): Promise<void> {
  await sendMessage({
    chatId,
    kind: 'poll',
    body: draft.question,
    poll: { options: draft.options, multiple: draft.multiple },
  })
}

/**
 * `PUT /api/messages/:id/vote` (W11) — the caller's answer to one option. The api owns the rule
 * (a single-choice poll replaces the answer and answers your own option by taking it back, a
 * multiple-choice one toggles that option), so nothing here decides; the response carries the whole
 * view, which is applied whole like the `message.poll` frame's would be — replacing, never merging,
 * so a client whose socket is down still shows its own tally. Throws, so the thread can say the
 * vote did not land.
 */
export async function voteOnPoll(chatId: string, messageId: string, optionIndex: number): Promise<void> {
  const { message } = await api.vote(messageId, { optionIndex })
  if (message.poll) useChatStore.getState().applyPoll(chatId, messageId, message.poll)
}

/**
 * `PATCH /api/messages/:id` (W2) — the composer's edit save. The store moves from the response, not
 * from the `message.edited` frame: this device's own frame is the same reconciliation arriving a
 * moment later, so applying the REST view first makes it idempotent. Throws, so the composer keeps
 * the draft and keeps edit mode.
 */
export async function saveEditedMessage(messageId: string, body: string): Promise<void> {
  const { message } = await api.editMessage(messageId, { body })
  useChatStore.getState().applyEdited(message)
}

/**
 * `DELETE /api/messages/:id` (W2) — delete for everyone. The server keeps the row as a tombstone;
 * the client marks the stored copy so the bubble, the list preview and the reply quotes all read
 * `deletedAt` instead of the body. The `message.deleted` frame repeats this harmlessly.
 */
export async function deleteMessageForEveryone(chatId: string, messageId: string): Promise<void> {
  await api.deleteMessage(messageId)
  useChatStore.getState().applyDeleted(chatId, messageId)
}

/**
 * `PUT /api/messages/:id/reaction` (W9) — the caller's one reaction; a second emoji replaces the
 * first, which is what the api's key says. The response carries the same whole aggregated list as
 * the `message.reaction` frame, so the stored row moves from the server's answer — a client whose
 * socket is down still shows its own chip. Throws, so the caller can say the refusal out loud.
 */
export async function reactToMessage(chatId: string, messageId: string, emoji: string): Promise<void> {
  const { message } = await api.setReaction(messageId, { emoji })
  useChatStore.getState().applyReactions(chatId, messageId, message.reactions)
}

/**
 * `DELETE /api/messages/:id/reaction` (W9) — clears the caller's own reaction, and is a harmless
 * no-op when there was none. Same "apply the server's list" rule as `reactToMessage`.
 */
export async function clearReaction(chatId: string, messageId: string): Promise<void> {
  const { message } = await api.clearReaction(messageId)
  useChatStore.getState().applyReactions(chatId, messageId, message.reactions)
}

/**
 * W4 — the clipboard. `false` is a refusal, not a swallow: `navigator.clipboard` needs a secure
 * context and a user gesture, so a rejected promise is a failure the caller has to say out loud
 * (`chat.copyFailed`).
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/**
 * A message's own words to the clipboard (W4). Callers offer this only where `canCopyMessage` says
 * there is a body, so what is pasted is the message verbatim and not a rendering of it.
 */
export function copyMessage(message: MessageView): Promise<boolean> {
  return copyText(message.body ?? '')
}

/**
 * `POST /api/messages/forward` (W4) — one copy per (message, target chat), authored by the caller
 * and marked `forwarded`; the originals are untouched. The copies are pushed like any new message,
 * and the response is applied too, so a target chat shows its copy even while the socket is down.
 * Returns how many chats the call went to — the number the thread's confirmation names.
 */
export async function forwardMessages(messageIds: string[], chatIds: string[]): Promise<number> {
  const { messages } = await api.forwardMessages({ messageIds, chatIds })
  for (const message of messages) useChatStore.getState().upsertMessage(message)
  return chatIds.length
}

export function markChatRead(chatId: string, messageId: string): void {
  if (useChatStore.getState().chats[chatId]?.lastReadId === messageId) return
  useChatStore.getState().clearUnread(chatId, messageId)
  sendRead(chatId, messageId)
  void api.markRead(chatId, { messageId }).catch(() => undefined)
}

export async function uploadAndSendMedia(input: {
  chatId: string
  file: File
  kind: MessageView['kind']
  replyToId?: string | null
  onProgress?: (fraction: number) => void
}): Promise<void> {
  const { media } = await uploadWithProgress<MediaResponse>({
    path: `/api/chats/${encodeURIComponent(input.chatId)}/media`,
    blob: input.file,
    filename: input.file.name || 'upload',
    onProgress: input.onProgress,
  })
  await sendMessage({
    chatId: input.chatId,
    kind: media.kind === 'voice' ? 'voice' : input.kind,
    body: media.kind === 'file' ? input.file.name : null,
    mediaId: media.id,
    media,
    replyToId: input.replyToId ?? null,
  })
}

export async function uploadVoiceNote(input: {
  chatId: string
  blob: Blob
  filename: string;
  replyToId?: string | null
  onProgress?: (fraction: number) => void
}): Promise<void> {
  const { media } = await uploadWithProgress<MediaResponse>({
    path: `/api/chats/${encodeURIComponent(input.chatId)}/media`,
    blob: input.blob,
    filename: input.filename,
    onProgress: input.onProgress,
  })
  await sendMessage({
    chatId: input.chatId,
    kind: 'voice',
    body: null,
    mediaId: media.id,
    media,
    replyToId: input.replyToId ?? null,
  })
}

export async function uploadAvatar(file: File, onProgress?: (fraction: number) => void): Promise<void> {
  const { user } = await uploadWithProgress<UserResponse>({
    path: '/api/me/avatar',
    blob: file,
    filename: file.name || 'avatar',
    onProgress,
  })
  useAuthStore.getState().setUser(user)
}

/**
 * `PATCH /api/me {displayName}` — the account screen's name save. The api trims and rejects an empty
 * or >64-char name; the returned user is stored, so every surface showing the name updates at once.
 */
export async function saveDisplayName(displayName: string): Promise<void> {
  const { user } = await api.patchMe({ displayName })
  useAuthStore.getState().setUser(user)
}

/**
 * `PATCH /api/me/privacy` (W13) — the account screen's two switches.
 *
 * The response is the api's own view of the member (`MeResponse`), so both the user and the pair of
 * toggles are stored from it: the switches show what the server now holds, not what was asked for,
 * and a refusal never moves them.
 */
export async function savePrivacy(input: PrivacyRequest): Promise<void> {
  const { user, privacy } = await api.setPrivacy(input)
  useAuthStore.getState().setUser(user)
  useAuthStore.getState().setPrivacy(privacy)
}

function applyCallToken(response: CallTokenResponse): void {
  useCallStore.getState().setActive({
    call: response.call,
    token: response.token,
    wsUrl: response.wsUrl || LIVEKIT_URL,
  })
}

/**
 * Start an outgoing call.
 *
 * Desktop: the api's token becomes the in-app room (the overlay takes over from here).
 * Android: the same api call, then the call is handed to the native host — no token is applied and
 * no room is connected in this WebView, because the host activity's WebView joins it instead (two
 * joins from one device would put two participants in the room). If the host cannot open, the call
 * is ended rather than left live with nobody in it.
 */
export async function startCall(chatId: string, kind: CallKind): Promise<void> {
  const response = await api.startCall(chatId, { kind })
  applyCallToken(response)
}

/** Answer a ringing call: the join token becomes the in-app room. */
export async function acceptCall(callId: string): Promise<void> {
  const response = await api.joinCall(callId)
  applyCallToken(response)
}

/**
 * The one way a device rings a chat, from the thread's header and from a call-log row alike (W6),
 * so the two cannot drift.
 *
 * A call already running in the chat is *joined* — `POST /api/chats/:id/calls` starts a new row
 * every time, so ringing again would pull the chat into two rooms at once — and only then does a
 * new call start. The kind is the caller's, or the logged call's when a row calls back.
 */
export async function startOrJoinCall(chatId: string, kind: CallKind): Promise<void> {
  const active = useChatStore.getState().chats[chatId]?.activeCall
  if (active) {
    await acceptCall(active.id)
    return
  }
  await startCall(chatId, kind)
}

export async function declineCall(callId: string): Promise<void> {
  useCallStore.getState().setIncoming(null)
  await api.declineCall(callId).catch(() => undefined)
}

export async function endCall(callId: string): Promise<void> {
  useCallStore.getState().clearActive()
  await api.endCall(callId).catch(() => undefined)
}

/**
 * `GET /api/users` — the member directory (CONTRACT §4 *Directory*). Callers filter themselves
 * out; the picker offers the rest for a DM or a group.
 */
export async function loadDirectory(): Promise<UserView[]> {
  const { users } = await api.users()
  return users
}

export async function createDirectChat(userId: string): Promise<string> {
  const { chat } = await api.createChat({ kind: 'dm', memberIds: [userId] })
  useChatStore.getState().upsertChat(chat)
  return chat.id
}

export async function createGroupChat(memberIds: string[], title?: string): Promise<string> {
  const { chat } = await api.createChat({
    kind: 'group',
    memberIds,
    ...(title ? { title } : {}),
  })
  useChatStore.getState().upsertChat(chat)
  return chat.id
}

/**
 * `GET /api/chats/:id` for the details screen. A chat opened by deep link has no entry in the
 * store yet, and the screen must not render "Loading…" forever, so this always fetches (unlike
 * `openChat`, which skips work once the thread is loaded) and upserts the fresh `ChatView` — the
 * member list and `memberCount` the screen edits.
 */
export async function loadChatDetails(chatId: string): Promise<void> {
  const { chat } = await api.chat(chatId)
  useChatStore.getState().upsertChat(chat)
}

/** `PATCH /api/chats/:id {title}` — a group's name. The api trims and rejects >80 chars. */
export async function renameChat(chatId: string, title: string): Promise<void> {
  const { chat } = await api.patchChat(chatId, { title })
  useChatStore.getState().upsertChat(chat)
}

/** `PATCH /api/chats/:id/mute {until}` — mute or unmute chat. */
export async function muteChat(chatId: string, until: number | null): Promise<void> {
  const { chat } = await api.muteChat(chatId, { until })
  useChatStore.getState().upsertChat(chat)
}

/**
 * `PATCH /api/chats/:id/pin {pinned}` (W5) — the caller's *own* pin, the chat list's first sort
 * key. The response's `pinnedAt` is the stamp the list sorts by, so the store moves from it rather
 * than from a local guess; nobody else's list moves.
 */
export async function pinChat(chatId: string, pinned: boolean): Promise<void> {
  const { chat } = await api.setChatPin(chatId, pinned)
  useChatStore.getState().upsertChat(chat)
}

/**
 * `PATCH /api/chats/:id/pinned-message {messageId}` (W5) — the *chat's* one pinned message, any
 * member's to set or clear. `null` takes the bar down. The response carries the new `pinnedMessage`
 * (and the `chat.pinned` frame repeats it for every member), so the bar appears on the server's
 * answer, not on the press.
 */
export async function pinMessage(chatId: string, messageId: string | null): Promise<void> {
  const { chat } = await api.setPinnedMessage(chatId, messageId)
  useChatStore.getState().upsertChat(chat)
}

/**
 * `PATCH /api/chats/:id/disappearing {seconds}` (W13) — the chat's timer, `null` for off. Any member
 * may set it, and the returned `ChatView` carries the value, so the details row and the thread's
 * line both move off the server's answer (`chat.disappearing` repeats it to everyone else).
 */
export async function setChatDisappearing(chatId: string, seconds: number | null): Promise<void> {
  const { chat } = await api.setDisappearing(chatId, seconds)
  useChatStore.getState().upsertChat(chat)
}

/**
 * `POST /api/chats/:id/export` (W14) — assemble this member's copy of the conversation. The
 * archive's `url` is a signed, expiring link, so the caller hands it straight to the browser with
 * no bearer token; the api keeps the file for a week and builds a fresh one on every ask, so
 * nothing here is cached or stored. An export is a copy: no store moves, and the chat is untouched.
 */
export async function exportChat(chatId: string): Promise<ExportView> {
  const response = await api.exportChat(chatId)
  return response.export
}

/**
 * A group's photo. The api's `PATCH /api/chats/:id` only accepts media whose `scope` is `chat` and
 * whose `chat_id` is this chat (apps/api/src/routes/chats.ts), so the bytes go to the chat's own
 * media endpoint — the same `uploadWithProgress` path `uploadAvatar` uses — and its id becomes the
 * `avatarMediaId`. Uploading to `/api/me/avatar` would set the *caller's* photo and be rejected
 * here as `scope:"avatar"`.
 */
export async function setChatPhoto(
  chatId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const { media } = await uploadWithProgress<MediaResponse>({
    path: `/api/chats/${encodeURIComponent(chatId)}/media`,
    blob: file,
    filename: file.name || 'photo',
    onProgress,
  })
  const { chat } = await api.patchChat(chatId, { avatarMediaId: media.id })
  useChatStore.getState().upsertChat(chat)
}

/** `POST /api/chats/:id/members` — the api is idempotent, so re-adding a member is a no-op. */
export async function addChatMembers(chatId: string, userIds: string[]): Promise<void> {
  const { chat } = await api.addChatMembers(chatId, { userIds })
  useChatStore.getState().upsertChat(chat)
}

/**
 * `DELETE /api/chats/:id/members/:userId`. The response is a bare `{ok:true}`, but the row carries
 * `memberCount` and `members` that only the server can recompute, so the chat is re-read instead of
 * patched locally. Used both for removing someone else and for leaving yourself.
 */
export async function removeChatMember(chatId: string, userId: string): Promise<void> {
  await api.removeChatMember(chatId, userId)
  const { chat } = await api.chat(chatId)
  useChatStore.getState().upsertChat(chat)
}

/**
 * Leaving is removing yourself — the api's same `DELETE .../members/:userId`. The chat is dropped
 * from the store after the server confirms, so leaving a chat does not leave it in the list (which
 * `setChats` would otherwise keep as a locally-known chat).
 */
export async function leaveChat(chatId: string): Promise<void> {
  const meId = useAuthStore.getState().user?.id
  if (!meId) return
  await api.removeChatMember(chatId, meId)
  useChatStore.getState().removeChat(chatId)
}

export function setActiveChat(chatId: string | null): void {
  useUiStore.getState().setActiveChatId(chatId)
}

/**
 * Drops the local session, best-effort removes this device's push subscription, and stops the
 * native background link.
 *
 * The link has to go first and for good: it holds `wss://<api>/api/ws` with the device token from
 * the native SharedPreferences (`LinkService`), so leaving it armed means the phone this user just
 * signed out of keeps ringing for their chats, and the api keeps reporting them online.
 * `Ring.stopBackgroundLink()` closes the socket, stops the silent keep-alive and the service, and
 * clears the "keep calls ringing" flag — which is also what keeps `BootReceiver` from re-arming it
 */
export async function signOut(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) {
        await api.unsubscribePush({ endpoint: subscription.endpoint }).catch(() => undefined)
        await subscription.unsubscribe().catch(() => undefined)
      }
    }
  } catch {
    /* best effort */
  }
  disconnectSocket()
  useAuthStore.getState().clearSession()
  useChatStore.getState().reset()
  useSearchStore.getState().clear()
  useCallStore.getState().clearActive()
}
